/**
 * Correcting a project that already exists.
 *
 * The register could create a project and then only ever toggle its status or
 * drag its progress bar, which was survivable while every project was typed in
 * here by hand and stops being survivable the moment twenty-three of them
 * arrive from the old CRM with names, budgets and deadlines somebody else
 * chose. Data you cannot correct is data you stop trusting.
 *
 * One rule makes an edit form safe on this record: only what changed is sent.
 * A project carries two columns this dialog never shows - `progressPct`, which
 * the overview slider writes, and `status`, which the row menu and the roadmap
 * write - and both of them can move while the dialog sits open. A full-object
 * PATCH would post the values that were true when it opened and quietly undo
 * whatever happened since; `projectPatch` posts the operator's edit and nothing
 * else. Nothing to send is a normal outcome, not an error: somebody opened the
 * form, read it and closed it.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { updateProject, type Project } from "../../../lib/projects";
import { Btn, Modal, useToast } from "../../ui";
import { PopHost } from "../../shell/Pop";
import {
  ProjectForm,
  clearedFigure,
  projectPatch,
  projectToForm,
  type ProjectFormValues,
} from "./ProjectForm";

export function EditProjectModal({
  project,
  onClose,
  onSaved,
}: {
  project: Project;
  onClose: () => void;
  onSaved: (p: Project) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [values, setValues] = useState<ProjectFormValues>(() => projectToForm(project));
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !values.name.trim()) return;

    const patch = projectPatch(project, values);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setBusy(true);
    try {
      onSaved(await updateProject(project.id, patch));
      onClose();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("projects.editTitle")} label={t("projects.editTitle")} onClose={onClose}>
      {/* The billing-type field is a `Pop` menu, and a `Pop` without a host
          above it is a control that can be focused and pressed and never
          opens, silently. `Modal` mounts none, and this dialog is opened from
          screens that do not all have one of their own - so it is mounted
          here rather than left to the caller. */}
      <PopHost>
        <form onSubmit={submit}>
          <ProjectForm
            value={values}
            onChange={setValues}
            mode="edit"
            figureHint={clearedFigure(project, values) ? t("projects.figureNotClearable") : undefined}
          />

          <div className="crm-m-foot">
            <Btn type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Btn>
            <Btn type="submit" tone="dark" disabled={busy || !values.name.trim()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("common.save")}
            </Btn>
          </div>
        </form>
      </PopHost>
    </Modal>
  );
}
